/**
 * **Cari bill** — menemukan kembali transaksi yang sudah lewat.
 *
 * ### ⭐ Lubang yang ditutupnya
 * Refund (2026-08-23) hanya bisa dijangkau dari bill yang **barusan lunas di terminal itu**. Tamu
 * yang komplain besok — atau yang datang ke till sebelah — tak punya jalan sama sekali. Pintunya
 * ada, tapi cuma terbuka beberapa menit di satu mesin.
 *
 * ⚠️ Satu layar ini menjawab tiga kebutuhan harian sekaligus: **cetak ulang struk lama** · lihat
 * rincian transaksi · telusuri selisih saat tutup sesi.
 *
 * ### ⛔ Ini BUKAN laporan
 * Yang tampil bill satu per satu, tak pernah agregat — tak ada satu pun angka total di layar ini.
 * Yang menjumlahkan tetap X/Z report di balik izin `report:view`. Menggabungkan keduanya berarti
 * salah satunya salah gerbang.
 *
 * ### ⭐ Tanda dulu, nomor pesanan belakangan
 * Kasir mencari dengan yang ia INGAT: *"meja 5"*, *"antrean 7"*, *"atas nama Budi"*. Nomor pesanan
 * (`UI/000123/1`) hampir tak pernah jadi titik mulai — sama seperti di layar dapur, ia jaring
 * terakhir, bukan judul.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { FoundOrder } from '../api/client';
import { formatRupiah } from '../format';
import { hasPermission } from '../api/session';
import { PrintButton } from './PrintButton';

export interface BillSearchProps {
  readonly outletCode: string;
  readonly deviceCode: string;
  /** Tanggal usaha sesi berjalan — titik mulai pencarian. */
  readonly businessDate?: string;
  readonly onClose: () => void;
  /** Membuka dialog refund untuk bill terpilih. */
  readonly onRefund: (orderId: string) => void;
}

/** Berapa hari ke belakang tombol cepat menyisir. ⚠️ Server tetap yang menegakkan batasnya. */
const CEPAT = [0, 1, 7] as const;

export function BillSearch(p: BillSearchProps) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [hari, setHari] = useState<(typeof CEPAT)[number]>(0);
  const [rows, setRows] = useState<readonly FoundOrder[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dibuka, setDibuka] = useState<string | null>(null);

  const cari = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      /*
       * ⛔ Tanggal mulai dari SESI KAS, bukan jam tablet. Outlet tutup pukul 02:00: pencarian yang
       * memakai `new Date()` akan melewatkan seluruh bill malam itu, dan kasir menyimpulkan
       * billnya hilang — persis saat tamunya berdiri menunggu.
       */
      const to = p.businessDate ?? '';
      const from = to === '' ? '' : geser(to, -hari);
      const r = await api.searchOrders(p.outletCode, {
        ...(from === '' ? {} : { from }),
        ...(to === '' ? {} : { to }),
        q,
      });
      setRows(r.rows);
      setTruncated(r.truncated);
    } catch (e) {
      /* ⛔ Pesan server apa adanya: `ORDER-400-RANGE` sudah menyebut batas & jalan keluarnya. */
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [hari, p.businessDate, p.outletCode, q]);

  /* Sekali saat dibuka, lalu hanya saat kasir menekan Cari / mengubah rentang. */
  useEffect(() => { void cari(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hari]);

  return (
    <div className="dialog bill-search" role="dialog" data-testid="bill-search">
      <h2>{t('cashier.find.title')}</h2>

      <form
        className="bill-search-bar"
        onSubmit={(e) => { e.preventDefault(); void cari(); }}
      >
        <input
          data-testid="bill-search-q"
          /* ⭐ Petunjuknya menyebut apa yang kasir INGAT, bukan nama kolom database. */
          placeholder={t('cashier.find.hint')}
          value={q}
          onChange={(e) => { setQ(e.target.value); }}
        />
        <button type="submit" className="action-primary" data-testid="bill-search-go" disabled={busy}>
          {busy ? t('common.loading') : t('cashier.find.search')}
        </button>
      </form>

      <div className="bill-search-range">
        {CEPAT.map((d) => (
          <button
            key={d}
            type="button"
            className={hari === d ? 'level-chip on' : 'level-chip'}
            data-testid={`bill-range-${d}`}
            onClick={() => { setHari(d); }}
          >
            {t(`cashier.find.range.${d}`)}
          </button>
        ))}
      </div>

      {error !== null && <p className="error" data-testid="bill-search-error">{error}</p>}

      {!busy && rows.length === 0 && error === null && (
        <p className="empty" data-testid="bill-search-empty">{t('cashier.find.empty')}</p>
      )}

      <ul className="bill-list">
        {rows.map((r) => {
          const sudah = Number(r.refundedTotal) > 0;
          return (
            <li key={r.orderId} data-testid={`bill-${r.orderNo}`}>
              <button
                type="button"
                className="bill-row"
                data-testid={`bill-open-${r.orderNo}`}
                onClick={() => { setDibuka(dibuka === r.orderId ? null : r.orderId); }}
              >
                <span className="bill-tag">{tanda(r)}</span>
                <span className="bill-meta">
                  {jam(r.closedAt)}
                  {r.closedByName === null ? '' : ` · ${r.closedByName}`}
                </span>
                <span className="bill-total">{formatRupiah(r.grandTotal)}</span>
                {/*
                  ⭐⭐ Tanda "sudah pernah dikembalikan" ada DI BARIS, bukan cuma di rincian: kasir
                  yang membukanya lalu baru tahu sudah terlanjur menjanjikan sesuatu ke tamunya.
                */}
                {sudah && (
                  <span className="bill-refunded" data-testid={`bill-refunded-${r.orderNo}`}>
                    −{formatRupiah(r.refundedTotal)}
                  </span>
                )}
              </button>

              {dibuka === r.orderId && (
                <div className="bill-actions" data-testid={`bill-actions-${r.orderNo}`}>
                  <span className="bill-no">{r.orderNo}</span>
                  <PrintButton orderId={r.orderId} docType="receipt" deviceCode={p.deviceCode} />
                  {/*
                    ⛔ Tombol refund disaring IZIN, bukan ditampilkan lalu ditolak: tombol yang
                    terlihat tapi selalu gagal mengajari kasir bahwa penolakan itu wajar.
                  */}
                  {hasPermission('order:refund') && r.status === 'paid' && (
                    <button
                      type="button"
                      className="action-secondary"
                      data-testid={`bill-refund-${r.orderNo}`}
                      onClick={() => { p.onRefund(r.orderId); }}
                    >
                      {t('cashier.refund.button')}
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/*
        ⛔ Daftar yang terpotong DIKATAKAN. Yang dipotong diam-diam membuat kasir menyimpulkan
        billnya tak ada, lalu berhenti mencari — dan itu kesimpulan yang salah dengan biaya nyata.
      */}
      {truncated && (
        <p className="hint" data-testid="bill-search-truncated">{t('cashier.find.truncated')}</p>
      )}

      <div className="dialog-actions">
        <button type="button" className="action-primary" data-testid="bill-search-close"
                onClick={p.onClose}>
          {t('cashier.find.close')}
        </button>
      </div>
    </div>
  );
}

/**
 * Tanda yang dikenal orang — sama urutannya dengan judul kartu KDS, dan itu disengaja: kasir dan
 * dapur harus menyebut bill yang sama dengan kata yang sama.
 */
function tanda(r: FoundOrder): string {
  if (r.tableCode !== null && r.tableCode !== '') return r.tableCode;
  if (r.parkLabel !== null && r.parkLabel !== '') return r.parkLabel;
  return r.orderNo;
}

/** Jam tutup, jam dinding lokal. ⚠️ Tampilan saja — tanggalnya tetap TANGGAL USAHA dari server. */
function jam(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Geser tanggal usaha N hari. ⚠️ Dihitung di UTC dari string `YYYY-MM-DD`, bukan dari `Date` lokal:
 * tanggal usaha bukan momen waktu, dan menggesernya lewat zona waktu perangkat akan meleset satu
 * hari di separuh dunia.
 */
function geser(businessDate: string, hari: number): string {
  const t = Date.parse(`${businessDate}T00:00:00Z`);
  if (Number.isNaN(t)) return businessDate;
  return new Date(t + hari * 86_400_000).toISOString().slice(0, 10);
}
